
import React from 'react';
import { siteConfig } from '../config/site';
import { Check, Diamond } from 'lucide-react';
import { useLanguage } from '../App';

const VIPSection: React.FC = () => {
  const { t, openBooking } = useLanguage();
  
  const videoSources = [
    {
      src: "https://player.vimeo.com/video/1157690820?badge=0&autopause=0&player_id=0&app_id=58479",
      title: "Mastery Reel",
      aspect: "aspect-[9/16]" // Portrait Reel
    },
    {
      src: siteConfig.videos.homeFeatured,
      title: "Signature Film",
      aspect: "aspect-[9/16]" // Match the portrait ratio of the reel next to it
    }
  ];

  return (
    <section id="vip" className="relative py-20 sm:py-32 px-4 sm:px-6 overflow-hidden bg-primary border-t border-white/5">
      <div className="absolute inset-0 bg-accent/[0.03] pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16 sm:mb-24 reveal reveal-up active">
          <h3 className="text-accent font-black tracking-[0.4em] uppercase text-[10px] mb-4">{t.eliteExclusivity}</h3>
          <h2 className="text-4xl sm:text-6xl font-serif font-bold mb-6 uppercase tracking-tight leading-tight">{t.vipExperience}</h2>
          <div className="w-16 h-px bg-accent/20 mx-auto"></div>
        </div>

        {/* Pricing Card */}
        <div className="max-w-3xl mx-auto mb-20 sm:mb-32 reveal reveal-up active">
          <div className="relative bg-secondary/30 p-8 sm:p-20 border border-white/5 text-center group hover:border-accent/10 transition-all duration-700 shadow-3xl backdrop-blur-md overflow-hidden">
            <Diamond className="mx-auto text-accent mb-8 w-10 h-10 opacity-30 group-hover:opacity-60 transition-opacity" />
            
            <h4 className="text-2xl sm:text-4xl font-serif font-bold mb-6 uppercase tracking-[0.2em]">{t.eliteSignatureCall}</h4>
            <div className="flex items-baseline justify-center gap-2 mb-10 sm:mb-12">
              <span className="text-6xl sm:text-8xl font-serif font-bold accent-text-gradient italic">$180</span>
              <span className="text-white/10 text-[9px] font-black uppercase tracking-[0.3em]">{t.singleInvestment}</span>
            </div>

            <p className="text-white/40 max-w-md mx-auto mb-12 leading-relaxed italic font-light text-base sm:text-lg">
              "{t.vipQuote}"
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mb-14 text-left max-w-xl mx-auto px-4 sm:px-0">
              {t.vipChecklist.map((item: string, i: number) => (
                <div key={i} className="flex items-center gap-3 text-white/50 group/item">
                  <Check className="w-3 h-3 text-accent/40 group-hover/item:text-accent transition-colors shrink-0" />
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold">{item}</span>
                </div>
              ))}
            </div>

            <button 
              onClick={openBooking}
              className="inline-block w-full sm:w-auto px-12 py-6 bg-accent text-black font-black uppercase tracking-[0.2em] text-[10px] hover:bg-white transition-all duration-700 shadow-2xl"
            >
              {t.requestAppointment}
            </button>
          </div>
        </div>

        {/* Immersive Video Grid - Standardized Portrait Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-12 max-w-5xl mx-auto items-start reveal reveal-up active">
          {videoSources.map((video, i) => (
            <div key={i} className="relative group p-0.5 sm:p-1 bg-accent/10 border border-white/5 shadow-3xl overflow-hidden">
              <div className={`${video.aspect} w-full overflow-hidden bg-black relative`}>
                <iframe 
                  src={video.src} 
                  className="absolute inset-0 w-full h-full border-0 scale-[1.02]"
                  title={video.title}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
                
                {/* Visual Label */}
                <div className="absolute bottom-4 left-4 z-20">
                   <span className="px-3 py-1 bg-black/60 backdrop-blur-md text-[7px] text-accent/80 font-black tracking-widest uppercase border border-accent/10">
                     {i === 0 ? 'MASTERY REEL' : 'FEATURED FILM'}
                   </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VIPSection;
